import { createDateTimeFormat, hourMinuteSeparatorOverride, languageOf } from '@hqo/date-time/core';

// Localized hour/minute hint letters for the time-picker placeholder mask. Only languages whose
// conventional abbreviation differs from HH/MM are listed; every other language falls back to HH:MM.
// This mirrors the repo's own `exp_date_placeholder` convention of localized format-hint letters
// (MM/AA, MM/JJ, HH/ÉÉ …).
const HOUR_HINT_BY_LANGUAGE: Record<string, string> = {
  nb: 'TT', // Timer
  nl: 'UU', // Uur
  pl: 'GG', // Godziny
  hu: 'ÓÓ', // Óra
};
const MINUTE_HINT_BY_LANGUAGE: Record<string, string> = {
  hu: 'PP', // Perc
};
const DEFAULT_HOUR_HINT = 'HH';
const DEFAULT_MINUTE_HINT = 'MM';

// A representative time (09:05) so the hour and minute produce two visible groups. The value itself
// is irrelevant — only the localized STRUCTURE (separator, day period, field order) is read off it.
const SAMPLE_TIME = new Date(2026, 0, 1, 9, 5, 0, 0);

/**
 * Builds a time-picker placeholder that mirrors the locale's ACTUAL rendered time format, so the hint
 * matches the values the picker itself shows: French `HH h MM`, en-US `HH:MM AM`, en-GB `HH:MM`,
 * Hungarian `ÓÓ:PP`. Derived from the same CLDR pipeline as the picker labels (@hqo/date-time) rather
 * than a hand-written per-locale mask, which drifts from the display (e.g. a colon where France shows
 * an `h`, or a hidden day period on a 12-hour clock).
 */
export const getTimePlaceholder = (locale: string): string => {
  const language = languageOf(locale);
  const hourHint = HOUR_HINT_BY_LANGUAGE[language] ?? DEFAULT_HOUR_HINT;
  const minuteHint = MINUTE_HINT_BY_LANGUAGE[language] ?? DEFAULT_MINUTE_HINT;

  const parts = createDateTimeFormat(locale, { timeStyle: 'short' }).formatToParts(SAMPLE_TIME);
  const hasDayPeriod = parts.some(part => part.type === 'dayPeriod');
  // France et al. render `09 h 05`; the `h` override is suppressed on 12-hour clocks (they keep `:`),
  // matching @hqo/date-time's own applyLocaleTimeConventions rule.
  const separatorOverride = hasDayPeriod ? undefined : hourMinuteSeparatorOverride(locale);

  return parts
    .map((part, index) => {
      if (part.type === 'hour') {
        return hourHint;
      }
      if (part.type === 'minute') {
        return minuteHint;
      }
      if (
        part.type === 'literal' &&
        separatorOverride &&
        parts[index - 1]?.type === 'hour' &&
        parts[index + 1]?.type === 'minute'
      ) {
        return separatorOverride;
      }
      return part.value;
    })
    .join('');
};
